import clsx from 'clsx';
import { useMultipleSelection, useSelect } from 'downshift';

import { IValueOptionsShape } from '~services/entity-definitions/interface';

export type MultipleSelectProps = {
  name: string;
  defaultValue: string | string[] | number | number[] | null;
  options: IValueOptionsShape;
  onChange?: (value: string[] | number[]) => void;
  placeholder?: string;
};

export const MultipleSelect = ({ defaultValue, options, onChange, placeholder }: MultipleSelectProps) => {
  function getOptionsFilter(selectedItems) {
    return option => {
      return selectedItems.indexOf(option) < 0;
    };
  }

  function getDefaultSelected() {
    // fix to ensure defaults are an array
    const defaultS = defaultValue !== undefined ? defaultValue.toString().split(',') : [];
    let list = [];
    options.forEach(option => {
      if (defaultS.indexOf(option.value.toString()) >= 0) list.push(option);
    });
    return list;
  }

  function updateFieldValue(selected) {
    const ids = [];
    Object.keys(selected).forEach(key => {
      ids.push(selected[key].value);
    });
    onChange(ids);
  }

  const { getSelectedItemProps, getDropdownProps, addSelectedItem, removeSelectedItem, selectedItems } =
    useMultipleSelection({ initialSelectedItems: getDefaultSelected() });
  const items = options.filter(getOptionsFilter(selectedItems));

  const { isOpen, selectedItem, getToggleButtonProps, getMenuProps, highlightedIndex, getItemProps } = useSelect({
    selectedItem: null,
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    items,
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useSelect.stateChangeTypes.ItemClick:
          if (selectedItem) {
            addSelectedItem(selectedItem);
            updateFieldValue([...selectedItems, selectedItem]);
          }
          break;
        default:
          break;
      }
    },
  });

  return (
    <div className="w-[592px] relative overflow-visible">
      <div className="flex flex-col gap-1">
        <div className="shadow-sm bg-white inline-flex gap-2 items-center flex-wrap p-1.5 border border-solid">
          {selectedItems.map((selectedItemForRender, index) => {
            return (
              <span
                className="bg-gray-200 rounded-md py-1 px-2 focus:bg-red-400 text-sm"
                key={`selected-item-${index}`}
                {...getSelectedItemProps({
                  selectedItem: selectedItemForRender,
                  index,
                })}
              >
                {selectedItemForRender.label}
                <span
                  className="px-1 cursor-pointer border"
                  onClick={e => {
                    e.stopPropagation();
                    removeSelectedItem(selectedItemForRender);
                    const list = [...selectedItems];
                    list.splice(index, 1);
                    updateFieldValue(list);
                  }}
                >
                  &#10005;
                </span>
              </span>
            );
          })}
          <div
            className="bg-gray-200 rounded-md py-1 px-2 focus:bg-red-400 text-sm cursor-pointer"
            {...getToggleButtonProps(getDropdownProps({ preventKeyAction: isOpen }))}
          >
            {placeholder || 'Please select'} &#8595;
          </div>
        </div>
      </div>
      <ul
        {...getMenuProps()}
        className="w2p-select absolute p-0 bg-white border-solid border max-h-80 overflow-y-auto w-inherit z-50"
      >
        {isOpen &&
          items.map((item, index) => (
            <li
              className={clsx(
                highlightedIndex === index && 'bg-gray-200',
                selectedItem === item && 'font-bold',
                'pl-1 pr-3 py-1 shadow-sm flex flex-col text-black text-sm',
              )}
              key={`${item.value}${index}`}
              {...getItemProps({ item, index })}
            >
              <span>{item.label}</span>
            </li>
          ))}
      </ul>
    </div>
  );
};

export default MultipleSelect;
